import { apiUrl } from '@constants';
import { markBackendRequestSent } from './backendRequestActivity';

export const IDP_V2_REFRESH_TOKEN_NAME = 'idpV2RefreshToken';
export const IDP_V2_SESSION_ID_NAME = 'idpV2SessionId';
export const IDP_V2_ACCESS_EXPIRES_AT_NAME = 'idpV2AccessExpiresAt';
export const IDP_V2_REFRESH_EXPIRES_AT_NAME = 'idpV2RefreshExpiresAt';

export type IdpV2LoginResponse = {
  access_token?: string | null;
  refresh_token?: string | null;
  session_id?: string | null;
  user_id?: string | null;
  access_expire_date?: string | null;
  refresh_expire_date?: string | null;
  email_verify?: boolean | null;
  mfa_required?: boolean | null;
  method?: 'email_otp' | 'totp' | 'totp_setup' | string | null;
  challenge_id?: string | null;
  email?: string | null;
  uri?: string | null;
};

type IdpV2Response<T> = {
  isSuccess?: boolean;
  statusCode?: number;
  message?: string;
  genericModel?: T;
};

type IdpV2PasswordResetInput = {
  user_id: string;
  hash: string;
  code: string;
  password: string;
};

export function getIdpV2BaseUrl(): string {
  if (import.meta.env.VITE_IDP_V2_BASE_URL) {
    return import.meta.env.VITE_IDP_V2_BASE_URL.replace(/\/$/, '');
  }

  return apiUrl.replace(/\/graphql\/?$/, '/idp/v2').replace(/\/$/, '');
}

export function persistIdpV2Tokens(result: IdpV2LoginResponse): void {
  let didUpdateStorage = false;
  if (result.refresh_token) {
    localStorage.setItem(IDP_V2_REFRESH_TOKEN_NAME, result.refresh_token);
    didUpdateStorage = true;
  }
  if (result.session_id) {
    localStorage.setItem(IDP_V2_SESSION_ID_NAME, result.session_id);
    didUpdateStorage = true;
  }
  if (result.access_expire_date) {
    localStorage.setItem(IDP_V2_ACCESS_EXPIRES_AT_NAME, result.access_expire_date);
    didUpdateStorage = true;
  }
  if (result.refresh_expire_date) {
    localStorage.setItem(IDP_V2_REFRESH_EXPIRES_AT_NAME, result.refresh_expire_date);
    didUpdateStorage = true;
  }
  if (didUpdateStorage) {
    document.dispatchEvent(new Event('storageChange'));
  }
}

export function clearIdpV2Tokens(): void {
  localStorage.removeItem(IDP_V2_REFRESH_TOKEN_NAME);
  localStorage.removeItem(IDP_V2_SESSION_ID_NAME);
  localStorage.removeItem(IDP_V2_ACCESS_EXPIRES_AT_NAME);
  localStorage.removeItem(IDP_V2_REFRESH_EXPIRES_AT_NAME);
  document.dispatchEvent(new Event('storageChange'));
}

export async function logoutFromIdpV2(accessToken?: string | null): Promise<void> {
  const refreshToken = localStorage.getItem(IDP_V2_REFRESH_TOKEN_NAME);
  if (!accessToken && !refreshToken) {
    return;
  }

  markBackendRequestSent();
  const response = await fetch(`${getIdpV2BaseUrl()}/auth/logout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      token: accessToken || ''
    },
    body: JSON.stringify({
      refresh_token: refreshToken || ''
    })
  });
  const json = await response.json().catch(() => ({})) as IdpV2Response<unknown>;

  if (!response.ok || !json.isSuccess) {
    throw new Error(json.message || 'Logout failed');
  }
}

async function requestIdpV2<T>(path: string, body: Record<string, unknown>, method = 'POST'): Promise<T> {
  markBackendRequestSent();
  const response = await fetch(`${getIdpV2BaseUrl()}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  const json = await response.json().catch(() => ({})) as IdpV2Response<T>;

  if (!response.ok || !json.isSuccess || !json.genericModel) {
    throw new Error(json.message || 'Login attempt failed');
  }

  return json.genericModel;
}

async function requestIdpV2WithoutModel(path: string, body: Record<string, unknown>, method = 'POST'): Promise<void> {
  markBackendRequestSent();
  const response = await fetch(`${getIdpV2BaseUrl()}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  });
  const json = await response.json().catch(() => ({})) as IdpV2Response<unknown>;

  if (!response.ok || !json.isSuccess) {
    throw new Error(json.message || 'Request failed');
  }
}

export async function loginWithIdpV2(email: string, password: string): Promise<IdpV2LoginResponse> {
  return requestIdpV2<IdpV2LoginResponse>('/auth/login', {
    loginName: email,
    password,
    client_id: localStorage.getItem('clientId') || undefined
  });
}

export async function refreshIdpV2Token(refreshToken: string): Promise<IdpV2LoginResponse> {
  return requestIdpV2<IdpV2LoginResponse>('/auth/refresh-token', {
    refresh_token: refreshToken
  });
}

export async function socialLoginWithIdpV2(provider: string, accessToken: string): Promise<IdpV2LoginResponse> {
  return requestIdpV2<IdpV2LoginResponse>('/auth/social-login', {
    provider,
    access_token: accessToken
  });
}

export async function verifyIdpV2EmailOtp(challengeId: string, code: string, rememberDevice: boolean): Promise<IdpV2LoginResponse> {
  return requestIdpV2<IdpV2LoginResponse>('/auth/verify-email-otp', {
    challenge_id: challengeId,
    code,
    remember_device: rememberDevice,
    client_id: localStorage.getItem('clientId') || undefined
  });
}

export async function resendIdpV2EmailOtp(challengeId: string): Promise<void> {
  return requestIdpV2WithoutModel('/auth/resend-email-otp', {
    challenge_id: challengeId,
    client_id: localStorage.getItem('clientId') || undefined
  });
}

export async function verifyIdpV2Totp(challengeId: string, code: string, rememberDevice: boolean): Promise<IdpV2LoginResponse> {
  return requestIdpV2<IdpV2LoginResponse>('/totp/verify', {
    challenge_id: challengeId,
    code,
    remember_device: rememberDevice,
    client_id: localStorage.getItem('clientId') || undefined
  });
}

export async function requestIdpV2PasswordReset(email: string): Promise<void> {
  return requestIdpV2WithoutModel('/auth/forgot-password', {
    loginName: email
  });
}

export async function updateIdpV2Password(input: IdpV2PasswordResetInput): Promise<void> {
  return requestIdpV2WithoutModel('/auth/update-password', input, 'PUT');
}
